import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { finalize, takeUntil } from 'rxjs/operators';

import { DocumentTypesUppercaseEnum } from '../link-document-modal/enums/ducument-types.enum';
import { DocumentTextTemplatesApiService } from 'projects/workspace/src/app/shared/services';
import { DocumentTextTemplateModel } from 'projects/workspace/src/app/shared/models';
import { DocumentTextLocationEnum } from 'projects/workspace/src/app/shared/enums';
import { SavePdfTextTemplateModalComponent } from '../save-pdf-text-template-modal/save-pdf-text-template-modal.component';
import { DangerModalComponent } from '../danger-modal/danger-modal.component';
import { CommonModalsActionsEnum } from '../common-modals-actions.enum';
import { DeleteTemplateModalData } from 'projects/workspace/src/app/shared/constants';

type UpdateRequestFunction = (fieldValue: string|{introText: string; subject: string}, fieldName: string) => Observable<any>;

@Component({
  selector: 'rnpl-add-pdf-text-modal',
  templateUrl: './add-pdf-text-modal.component.html',
})
export class AddPdfTextModalComponent implements OnInit, OnDestroy {

  public templatesList: DocumentTextTemplateModel[] = [];
  public introText: string = '';
  public outroText: string = '';
  public subject: string = '';
  public selectedMode: DocumentTextLocationEnum = DocumentTextLocationEnum.INTRO;
  public selectedTemplate: DocumentTextTemplateModel = null;
  public documentTextLocationEnum = DocumentTextLocationEnum;

  @ViewChild('textEditor', {static: true}) public textEditorEl;

  readonly destroy$: Subject<void> = new Subject();
  readonly templatesListLoading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  readonly submitRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  constructor(
    private dialog: MatDialog,
    private documentTextTemplatesApiService: DocumentTextTemplatesApiService,
    public dialogRef: MatDialogRef<AddPdfTextModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      mode: DocumentTextLocationEnum;
      documentType: DocumentTypesUppercaseEnum;
      introText: string;
      outroText: string;
      subject: string;
      subjectAllowed: boolean;
      textExist: boolean;
      updateRequest$: UpdateRequestFunction;
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });
  }

  ngOnInit(): void {
    this.selectedMode = this.data.mode;
    this.introText = this.data.introText;
    this.outroText = this.data.outroText;
    this.subject = this.data.subject;
    this.getTemplates();
  }

  private getTemplates(): void {
    this.documentTextTemplatesApiService.getTextTemplates(this.data.documentType, this.selectedMode)
      .pipe(
        finalize(() => this.templatesListLoading$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((templates: DocumentTextTemplateModel[]) => {
        this.templatesList = templates;
        this.checkSelectedTemplate(this.getFieldValue());
      });
  }

  public selectedModeChanged(): void {
    this.selectedTemplate = null;
    this.getTemplates();
  }

  public templateSelected(): void {
    if (this.selectedTemplate.location === DocumentTextLocationEnum.INTRO) {
      this.introText = this.selectedTemplate.text;
      if (this.selectedTemplate.subject) {
        this.subject = this.selectedTemplate.subject;
      }
    }
    if (this.selectedTemplate.location === DocumentTextLocationEnum.OUTRO) {
      this.outroText = this.selectedTemplate.text;
    }
  }

  public saveTemplate(): void {
    this.dialog.open(SavePdfTextTemplateModalComponent, {
      data: {
        templates: this.templatesList,
        selectedTemplate: this.selectedTemplate,
        documentType: this.data.documentType,
        mode: this.selectedMode,
        text: this.getFieldValue(),
        subject: this.subjectAllowed
          ? this.subject
          : null
      },
      disableClose: true
    }).afterClosed().subscribe((res: DocumentTextTemplateModel) => {
        if (res) {
          this.selectedTemplate = res;
          this.getTemplates();
        }
      });
  }

  public checkSelectedTemplate(fieldValue: string): void {
    if (this.templatesList.length && fieldValue) {
      const selectedTemplate = this.templatesList.find(tpl => tpl.text === fieldValue);
      if (selectedTemplate) {
        this.selectedTemplate = selectedTemplate;
      }
    }
  }

  public deleteTemplate(): void {
    if (!this.selectedTemplate) { return; }

    this.dialog.open(DangerModalComponent, { data: DeleteTemplateModalData, disableClose: true })
      .afterClosed()
      .subscribe((response: CommonModalsActionsEnum) => {
        if (response === CommonModalsActionsEnum.CONFIRM) {
          this.documentTextTemplatesApiService.deleteTextTemplate(this.selectedTemplate.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              this.selectedTemplate = null;
              this.getTemplates();
            });
        }
      });
  }

  public deleteText(): void {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: {
        title: 'PDF_SETTINGS.DELETE_TEXT',
        message: 'PDF_SETTINGS.DELETE_TEXT_MSG',
        confirmBtnText: 'BUTTON.DELETE',
        confirmBtnIcon: 'trash-2'
      },
      disableClose: true
    });

    dialog.afterClosed().subscribe((response: CommonModalsActionsEnum) => {
      if (response === CommonModalsActionsEnum.CONFIRM) {
        if (this.selectedMode === DocumentTextLocationEnum.INTRO) {
          this.introText = '';
          this.subject = '';
        }
        if (this.selectedMode === DocumentTextLocationEnum.OUTRO) {
          this.outroText = '';
        }

        this.submit(true);
      }
    });
  }

  public submit(clear = false): void {
    if (this.submitRequest$.getValue()) { return; }
    this.submitRequest$.next(true);

    const fieldName = {
      [DocumentTextLocationEnum.INTRO]: 'introText',
      [DocumentTextLocationEnum.OUTRO]: 'outroText',
    };

    const name = this.subjectAllowed
      ? 'introTextAndSubject'
      : fieldName[this.selectedMode];

    const value = this.subjectAllowed
      ? {
          introText: clear
            ? null
            : this.getFieldValue(),
          subject: clear
            ? null
            : this.subject
        }
      : clear
        ? null
        : this.getFieldValue();

    this.data.updateRequest$(value, name)
      .pipe(finalize(() => this.submitRequest$.next(false)))
      .subscribe(() => this.dialogRef.close());
  }

  public getFieldValue(): string {
    // const fieldValue = {
    //   [DocumentTextLocationEnum.INTRO]: this.introText,
    //   [DocumentTextLocationEnum.OUTRO]: this.outroText,
    // };
    //
    // return fieldValue[this.selectedMode];
    return this.textEditorEl.editorValue();
  }

  get editMode(): boolean {
    return this.data.textExist
      || this.selectedMode === DocumentTextLocationEnum.INTRO && !!this.data.introText
      || this.selectedMode === DocumentTextLocationEnum.OUTRO && !!this.data.outroText;
  }

  get subjectAllowed(): boolean {
    return this.data.hasOwnProperty('subject')
      && this.data.subjectAllowed
      && this.selectedMode === DocumentTextLocationEnum.INTRO;
  }

  get saveAllowed(): boolean {
    return ((this.getFieldValue() || this.subject) && !this.selectedTemplate)
      || (
        (this.selectedTemplate && (this.getFieldValue() !== this.selectedTemplate.text))
        || (this.subjectAllowed && this.selectedTemplate && this.selectedTemplate.subject !== this.subject)
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}

